/**
 * useVenueTaskCount: the open action items on the viewer's own venues, for the
 * sidebar's Venues badge.
 *
 * It runs in the app chrome on every page, so it is careful:
 *   - it fetches only for a viewer with ASSIGNED regions who holds read:venues
 *     and not read:all. An instructor (no read:venues) never makes a request
 *     that can only 403, and an admin never gets a national count nobody asked
 *     for;
 *   - a failure is swallowed and leaves the count undefined, so the badge is
 *     simply not drawn. A count endpoint must never break the rail;
 *   - undefined, not 0, while unknown, so nothing flashes a zero;
 *   - it refetches when the window regains focus, and note writes invalidate it.
 */
import { useQuery } from '@tanstack/react-query';

import { fetchOpenTaskCount } from '../../../api/venue-marketing';
import { useCurrentUser } from '../../../contexts/CurrentUserContext';
import { useViewerRegions } from '../../../hooks/use-viewer-regions';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../types/permissions';

/** Venue note and venue writes invalidate by this key. The cached entry adds the
 *  user's id, so a second sign-in in the same tab never shows the first's count. */
export const VENUE_TASK_COUNT_KEY = ['venues.task-count'] as const;

export function useVenueTaskCount(): number | undefined {
  const { hasPermission, loading } = usePermissions();
  const { regionIds } = useViewerRegions();
  const userId = useCurrentUser().currentUser?.id ?? null;
  const allowed =
    !loading &&
    userId !== null &&
    regionIds.length > 0 &&
    hasPermission(Permission.READ_VENUES) &&
    !hasPermission(Permission.READ_ALL);

  const { data } = useQuery({
    queryKey: [...VENUE_TASK_COUNT_KEY, userId],
    queryFn: fetchOpenTaskCount,
    enabled: allowed,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: false,
  });
  return allowed ? data : undefined;
}
